import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Plus, Trash, PencilSimple, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const VARS = ["{{first_name}}", "{{last_name}}", "{{full_name}}", "{{specialty}}", "{{clinic}}", "{{city}}", "{{state}}"];

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", stage_match: "", subject: "", body: "" });

  const load = async () => {
    const r = await api.get("/templates");
    setTemplates(r.data);
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing(null);
    setForm({ label: "", stage_match: "", subject: "", body: "" });
    setOpen(true);
  };

  const startEdit = (t) => {
    setEditing(t);
    setForm({ label: t.label, stage_match: t.stage_match || "", subject: t.subject, body: t.body });
    setOpen(true);
  };

  const save = async () => {
    if (!form.label.trim() || !form.subject.trim() || !form.body.trim()) {
      toast.error("Label, subject, body required");
      return;
    }
    if (editing) {
      await api.put(`/templates/${editing.id}`, form);
      toast.success("Template updated");
    } else {
      await api.post("/templates", form);
      toast.success("Template created");
    }
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/templates/${id}`);
    toast.success("Deleted");
    load();
  };

  const insertVar = (v) => setForm({ ...form, body: form.body + " " + v });

  return (
    <div className="p-8" data-testid="templates-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-1">Outreach</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Email Templates</h1>
          <p className="text-sm text-[#5C5E66] mt-2 max-w-2xl">Reusable email templates with merge variables. When you click Compose on a physician, the matching template is auto-filled and opens in your mail app (Outlook, Gmail, Apple Mail).</p>
        </div>
        <Button data-testid="new-template-btn" onClick={startNew} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none gap-2">
          <Plus size={16} weight="bold" /> New Template
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {templates.map((t) => (
          <div key={t.id} className="bg-white border border-[#E2E2E5] p-5" data-testid={`template-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-lg leading-tight">{t.label}</div>
                  {t.stage_match && (
                    <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-[#FF990014] text-[#C97000] border border-[#FF990066]">
                      {t.stage_match}
                    </span>
                  )}
                  {t.is_default && <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-[#F0F0F2] text-[#5C5E66]">DEFAULT</span>}
                </div>
                <div className="text-xs text-[#5C5E66] mt-2 font-medium">SUBJECT</div>
                <div className="text-sm font-medium">{t.subject}</div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => startEdit(t)} data-testid={`edit-template-${t.id}`} className="p-1.5 hover:bg-[#F0F0F2]"><PencilSimple size={14} /></button>
                <button onClick={() => remove(t.id)} className="p-1.5 hover:bg-[#C6282810] text-[#C62828]"><Trash size={14} /></button>
              </div>
            </div>
            <div className="text-xs text-[#5C5E66] mt-3 font-medium">BODY</div>
            <pre className="text-xs leading-relaxed whitespace-pre-wrap font-sans text-[#0A0B0F] mt-1 max-h-48 overflow-hidden bg-[#F7F7F8] border-l-2 border-[#E2E2E5] pl-3 py-2">{t.body}</pre>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none border-[#E2E2E5]" data-testid="template-edit-dialog">
          <DialogHeader className="border-b border-[#E2E2E5] pb-3">
            <DialogTitle className="font-display font-black tracking-tight text-2xl">
              {editing ? "Edit Template" : "New Template"}
            </DialogTitle>
            <DialogDescription className="sr-only">Create or edit an email template with merge variables.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Label *</Label>
              <Input data-testid="tpl-label" value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} className="rounded-none mt-1" placeholder="Day 1 — Introduction" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Stage Match (auto-pick for follow-ups containing this text)</Label>
              <Input data-testid="tpl-stage" value={form.stage_match} onChange={(e) => setForm({...form, stage_match: e.target.value})} className="rounded-none mt-1 font-mono" placeholder="Day 1" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Subject *</Label>
              <Input data-testid="tpl-subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} className="rounded-none mt-1" placeholder="Clinical research partnership — {{clinic}}" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Body *</Label>
                <div className="flex gap-1 flex-wrap">
                  {VARS.map(v => (
                    <button key={v} type="button" onClick={() => insertVar(v)} className="text-[10px] font-mono px-1.5 py-0.5 bg-[#F0F0F2] hover:bg-[#006CFF14] hover:text-[#006CFF]">{v}</button>
                  ))}
                </div>
              </div>
              <Textarea data-testid="tpl-body" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} rows={12} className="rounded-none mt-1 font-mono text-sm" />
            </div>
          </div>
          <DialogFooter className="border-t border-[#E2E2E5] pt-3">
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-template-btn" onClick={save} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
